import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { buscarIniciativas } from 'services/iniciativa.service';
import { HeaderElbb } from '../HomeElbb/components/Header';
import { HeroIniciativasElbb } from '../IniciativasElbb/components/HeroIniciativasElbb';
import { FiltrosIniciativasElbb } from '../IniciativasElbb/components/FiltrosIniciativasElbb';
import { IniciativaCardElbb } from '../HomeElbb/components/IniciativaCardElbb';
import '../IniciativasElbb/style.scss';

export function ClassificadasEtapa1Elbb() {
  const { user } = useAuth();
  const location = useLocation();
  const stateCategoria = (location.state as any)?.categoria;
  const [categoriaSelecionada, setCategoriaSelecionada] = useState(stateCategoria || 'todos');
  const [termoBusca, setTermoBusca] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [iniciativas, setIniciativas] = useState<any[]>([]);
  const [iniciativasFiltradas, setIniciativasFiltradas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const normalizarTexto = (valor?: string) =>
    (valor || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .trim();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  useEffect(() => {
    async function carregarIniciativas() {
      try {
        setLoading(true);
        const data = await buscarIniciativas({ categoria: 'ELBB' });
        const classificadas = data.filter(
          (ini: any) => ini.categoryPremio === 'ELBB' && ini.classificadaEtapa1 === true
        );
        setIniciativas(classificadas);
      } catch {
        setIniciativas([]);
      } finally {
        setLoading(false);
      }
    }

    if (user?._id) {
      carregarIniciativas();
    }
  }, [user?._id]);

  useEffect(() => {
    let resultado = [...iniciativas];

    if (categoriaSelecionada && categoriaSelecionada !== 'todos') {
      const perspectivaMap: Record<string, string> = {
        clientes: 'clientes',
        financeira: 'financeira',
        asg: 'asg',
        transformacao_digital_processos: 'transformacao digital e processos',
        pessoas_cultura: 'pessoas e cultura',
      };
      const perspectivaSelecionada = perspectivaMap[categoriaSelecionada] || '';
      resultado = resultado.filter((ini) => {
        const perspectivaIniciativa = normalizarTexto(ini.perspectiva || ini.tema);
        return perspectivaIniciativa === perspectivaSelecionada;
      });
    }

    if (termoBusca.trim()) {
      const termoLower = termoBusca.toLowerCase();
      resultado = resultado.filter((ini) =>
        ini.titulo?.toLowerCase().includes(termoLower) ||
        ini.descricao?.toLowerCase().includes(termoLower) ||
        ini.autores?.toLowerCase().includes(termoLower) ||
        ini.perspectiva?.toLowerCase().includes(termoLower) ||
        ini.tema?.toLowerCase().includes(termoLower) ||
        ini.diretoria?.toLowerCase().includes(termoLower)
      );
    }

    setIniciativasFiltradas(resultado);
    setPaginaAtual(1);
  }, [iniciativas, termoBusca, categoriaSelecionada]);

  const iniciativasPorPagina = 8;
  const totalPaginas = Math.ceil(iniciativasFiltradas.length / iniciativasPorPagina);
  const indexInicio = (paginaAtual - 1) * iniciativasPorPagina;
  const iniciativasVisiveis = iniciativasFiltradas.slice(indexInicio, indexInicio + iniciativasPorPagina);

  const handlePaginaChange = (pagina: number) => {
    setPaginaAtual(pagina);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div id="iniciativas-elbb-wrapper">
      <HeaderElbb currentPage="classificadas-etapa1" />

      <HeroIniciativasElbb
        categoriaSelecionada={categoriaSelecionada}
        onCategoriaChange={setCategoriaSelecionada}
        title="Classificadas Etapa 1"
        subtitle={<>Conheça as iniciativas classificadas<br />para a Etapa 1 do Prêmio ELBB 2026</>}
      />

      <FiltrosIniciativasElbb
        categoriaSelecionada=""
        onCategoriaChange={() => {}}
        termoBusca={termoBusca}
        onBuscaChange={setTermoBusca}
        onBuscar={() => {}}
      />

      <main className="iniciativas-elbb-main">
        <div className="iniciativas-elbb-main__container">
          <div className="iniciativas-elbb-main__header">
            <h2 className="iniciativas-elbb-main__count" tabIndex={0} aria-live="polite">
              {iniciativasFiltradas.length} iniciativas encontradas
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-8" role="status" aria-live="polite">
              <span className="text-primary-elbb">Carregando iniciativas...</span>
            </div>
          ) : iniciativasVisiveis.length === 0 ? (
            <div className="flex flex-col justify-center items-center py-12" role="status">
              <p className="text-lg text-gray-600 mb-2" tabIndex={0}>
                Nenhuma iniciativa classificada na Etapa 1 encontrada.
              </p>
            </div>
          ) : (
            <>
              <div className="iniciativas-elbb-main__grid">
                {iniciativasVisiveis.map((iniciativa) => (
                  <IniciativaCardElbb key={iniciativa.id} {...iniciativa} />
                ))}
              </div>

              {totalPaginas > 1 && (
                <nav className="iniciativas-elbb-main__pagination" role="navigation" aria-label="Paginação de iniciativas">
                  <button
                    className="pagination__btn pagination__btn--prev"
                    onClick={() => handlePaginaChange(paginaAtual - 1)}
                    disabled={paginaAtual === 1}
                    aria-label="Página anterior"
                  >
                    ‹
                  </button>

                  <div className="pagination__numbers">
                    {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((pagina) => (
                      <button
                        key={pagina}
                        className={`pagination__number ${paginaAtual === pagina ? 'active' : ''}`}
                        onClick={() => handlePaginaChange(pagina)}
                        aria-label={`Página ${pagina}`}
                        aria-current={paginaAtual === pagina ? 'page' : undefined}
                      >
                        {pagina}
                      </button>
                    ))}
                  </div>

                  <button
                    className="pagination__btn pagination__btn--next"
                    onClick={() => handlePaginaChange(paginaAtual + 1)}
                    disabled={paginaAtual === totalPaginas}
                    aria-label="Próxima página"
                  >
                    ›
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
